import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { createSale } from "@/api";
import { Button, FormContainer, FormSection } from "@/components";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { useCurrentStore } from "@/hooks";
import Customer from "./customer";
import Payment from "./payment";
import Price from "./price";
import ProductSearch from "./product-search";
import Products from "./products";
import { type CheckoutSchemaType, checkoutSchema } from "./schema";

const Checkout = () => {
	const { t } = useTranslation();
	const store = useCurrentStore();
	const queryClient = useQueryClient();
	const productSearchInputRef = useRef<HTMLInputElement>(null);
	useBreadCrumbContext([{ label: t("checkout.pageTitle") }]);
	const form = useForm<CheckoutSchemaType>({
		resolver: zodResolver(checkoutSchema),
	});
	const toastId = useRef<string>(null);

	useFormPersist("checkout", {
		watch: form.watch,
		setValue: form.setValue,
		storage: window.localStorage,
	});

	const { mutate } = useMutation({
		mutationFn: createSale,
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onSuccess: () => {
			form.reset();
			toast.success(t("global.saved"), { id: toastId?.current || undefined });
			queryClient.invalidateQueries({ queryKey: ["sales"] });
			productSearchInputRef?.current?.focus();
		},
		onError: () => {
			toast.error(t("global.savingError"), {
				id: toastId?.current || undefined,
			});
		},
	});

	const onSubmit = useCallback(
		(data: CheckoutSchemaType) => {
			mutate({ ...data, storeId: store?.id });
		},
		[mutate, store],
	);

	return (
		<FormProvider {...form}>
			<FormContainer
				title={t("checkout.pageTitle")}
				subtitle={t("checkout.pageSubtitle")}
				className="gap-2!"
				isNotForm
			>
				<FormSection className="gap-1!" title={t("checkout.products")}>
					<ProductSearch productSearchInputRef={productSearchInputRef} />
					<Products />
				</FormSection>
				<FormSection className="gap-1!" title={t("checkout.price")}>
					<Price />
				</FormSection>
				<FormSection className="gap-1!" title={t("checkout.customer")}>
					<Customer />
				</FormSection>
				<FormSection className="gap-1!" title={t("checkout.payment")}>
					<Payment />
				</FormSection>
				<div className="flex flex-row gap-4 justify-end">
					<Button onClick={() => form.reset()} type="button" variant="ghost">
						{t("checkout.resetButton")}
					</Button>
					<Button onClick={form.handleSubmit(onSubmit)}>
						{t("checkout.validateButton")}
					</Button>
				</div>
			</FormContainer>
		</FormProvider>
	);
};

export default Checkout;
